import { useStaticData } from "context/staticData";
import React, { useState } from "react";
import { getApiQueryKey } from "shared/datalayer";
import SchemaService from "shared/form/renderer/SchemaService";
import keyBy from "lodash/keyBy";
import ContactMode from "shared/enums/ContactMode";
import {
  getTransformedCriteriaForApi,
  processCriteriaForQueryBuilder
} from "apps/console/modules/program/configuration/selection/fsl/detail/components/searchCriteria";
import { useCopies } from "shared/hooks/useCopies";
import { useQueryClient } from "react-query";
import { leadCaptureFormFields } from "./utils";
import Validation from "shared/form/Validation";
import * as yup from "yup";
import { getYupTestForQuery } from "apps/console/modules/program/configuration/selection/fsl/detail/service";
import { useCommonFeatures } from "context/commonFeatures";
import {
  useCreateContactContext,
  useDownloadAttendeeListInContactContext,
  useGetContactContext,
  useGetEventParticipantsList,
  useUpdateContactContext,
  useUpdateContactContextStatus
} from "./dataloader";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import {
  MAX_DEFAULT_TEXTAREA_LENGTH,
  MAX_LICENSE_CAP_FOR_CONTACT_CONTEXT,
  MIN_DEFAULT_TEXTAREA_LENGTH,
  MIN_LICENSE_CAP_FOR_CONTACT_CONTEXT
} from "shared/utils/constants";
import { ContactContextLocation } from "shared/enums/ContactContextLocation";
import { CreateUpdateContactContextProps } from "./types";
import { downloadFileUsingData } from "shared/utils";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import { useEntity } from "apps/console/context/entity";
import Variant from "shared/enums/Variant";
import { FormikProps } from "formik";
import { ReportType } from "shared/enums/ReportType";

export function useContactContextService({
  baseApiUtil,
  requesterId,
  eventId,
  initialFormStateDisabled,
  contextId,
  applicableContactModes
}: CreateUpdateContactContextProps) {
  const formRef = React.useRef<FormikProps<any> | null>(null);
  const copies = useCopies();
  const { initConfirmationAlert } = useCommonFeatures();
  const queryClient = useQueryClient();
  const { eventDetails, registrationId } = useEntity();

  const [isFormDisabled, setIsFormDisabled] = useState(initialFormStateDisabled && !!contextId);
  const { mutateAsync: create } = useCreateContactContext(baseApiUtil);
  const { mutateAsync: update } = useUpdateContactContext(baseApiUtil);
  const { mutateAsync: updateStatus } = useUpdateContactContextStatus(baseApiUtil);
  const { mutateAsync: downloadAttendeelist } = useDownloadAttendeeListInContactContext(baseApiUtil, contextId);
  const { navigateBack } = useNavigateWrapper();
  const { data, refetch } = useGetContactContext({
    baseApiUtil,
    requesterId,
    contextId
  });

  const { data: participants } = useGetEventParticipantsList({
    baseApiUtil,
    requesterId
  });

  const staticData = useStaticData();

  const handleStatusUpdate = React.useCallback(async () => {
    if (data) {
      const contactMode = staticData.contactMode[data?.contactMode];
      let enableMessage: React.ReactNode = copies.get("context.location.enable.confirmation", {
        0: contactMode,
        1: data.label
      });
      let disableMessage = copies.get("context.location.disable.confirmation", { 0: contactMode, 1: data.label });

      if (data.contactMode === ContactMode.ACCESS_CONTROL && !data.accessCriteria) {
        enableMessage = (
          <div>
            <div>{copies.get("context.access.criteria.not.set.warning")}</div>
            {enableMessage}
          </div>
        );
      }

      initConfirmationAlert({
        message: data.enabled ? disableMessage : enableMessage,
        async handleConfirm(event) {
          await updateStatus({
            contextId: data.id,
            enabled: !data.enabled,
            uiVersion: data.uiVersion
          });
          queryClient.invalidateQueries(getApiQueryKey(baseApiUtil.getContactContextListByContactMode()));
          refetch();
        },
        handleClose() {}
      });
    }
  }, [baseApiUtil, copies, data, initConfirmationAlert, queryClient, refetch, staticData.contactMode, updateStatus]);

  const initialValues = React.useMemo(() => {
    function getInitalValueForContactMode() {
      if (applicableContactModes.length > 1) {
        return "";
      }
      return applicableContactModes[0];
    }

    return {
      label: data?.label || "",
      contactMode: data?.contactMode || getInitalValueForContactMode(),
      licenseCap: data?.licenseCap?.toString() || "",
      unlimitedLicenseCap: false,
      admins: data?.admins?.map(item => item.id) || [],
      licensees: data?.licensees?.map(item => item.id) || [],
      contactContextLocation: data?.contactContextLocation || "",
      eventId: data?.eventId || eventId,
      accessCriteria: data?.accessCriteria ? processCriteriaForQueryBuilder(data.accessCriteria) : null,
      licenseeCriteria: data?.licenseeCriteria ? processCriteriaForQueryBuilder(data.licenseeCriteria) : null,
      accessControlPolicy: data?.accessControlPolicy || "",
      eventParticipant: "",
      admins_add_new: "",
      admins_eventParticipant: "",
      licensees_add_new: "",
      licensees_eventParticipant: "",
      licenseeCriteria_enabled: data?.licenseeCriteria ? true : false,
      accessCriteria_enabled: data?.accessCriteria ? true : false,
      licenseCriteriaDescription: data?.licenseCriteriaDescription || "",
      accessCriteriaDescription: data?.accessCriteriaDescription || "",
      enforceTimeValidation: data?.enforceTimeValidation || false,
      enabled: data?.enabled || false
    };
  }, [data, eventId, applicableContactModes]);

  const handleCreateUpdate = React.useCallback(
    async (values: any) => {
      const unlimitedLicenseCap = values.licenseCap === "-1";
      const needsConfirmation = initialValues.enabled !== values.enabled;

      async function updateContext() {
        await update({
          ...data,
          ...values,
          unlimitedLicenseCap,
          requesterId,
          accessCriteria: values?.accessCriteria ? getTransformedCriteriaForApi(values.accessCriteria) : null,
          licenseeCriteria: values?.licenseeCriteria ? getTransformedCriteriaForApi(values.licenseeCriteria) : null
        });
        await refetch();
        if (initialFormStateDisabled) {
          setIsFormDisabled(true);
        } else {
          navigateBack();
        }
      }

      async function createContext() {
        await create({
          ...values,
          eventId,
          unlimitedLicenseCap,
          accessCriteria: values?.accessCriteria ? getTransformedCriteriaForApi(values.accessCriteria) : null,
          licenseeCriteria: values?.licenseeCriteria ? getTransformedCriteriaForApi(values.licenseeCriteria) : null
        });
        setIsFormDisabled(true);
      }

      async function handleCreateUpdateContext() {
        if (data) {
          try {
            await updateContext();
          } catch (error) {}
        } else {
          try {
            await createContext();
          } catch (error) {}
        }
        queryClient.invalidateQueries(getApiQueryKey(baseApiUtil.getContactContextListByContactMode()));
      }
      if (needsConfirmation) {
        let confirmationMessage: any = copies.get("context.location.enable.confirmation.and.save.changes", {
          0: staticData.contactMode[values?.contactMode],
          1: values.label
        });

        if (values.contactMode === ContactMode.ACCESS_CONTROL && !values.accessCriteria_enabled) {
          confirmationMessage = (
            <div>
              <div>{copies.get("context.access.criteria.not.set.warning")}</div>
              {confirmationMessage}
            </div>
          );
        }

        initConfirmationAlert({
          message: confirmationMessage,
          primaryButtonVariant: Variant.SUCCESS,
          handleConfirm: async () => {
            handleCreateUpdateContext();
          },
          handleClose: () => {
            formRef.current?.setFieldValue("enabled", false);
          }
        });
        return;
      }
      handleCreateUpdateContext();
    },
    [
      initialValues.enabled,
      data,
      queryClient,
      baseApiUtil,
      update,
      requesterId,
      refetch,
      initialFormStateDisabled,
      navigateBack,
      create,
      eventId,
      initConfirmationAlert,
      copies,
      staticData.contactMode
    ]
  );

  const options = React.useMemo(() => {
    let contactMode = SchemaService.getOptionsListFromStaticData(staticData.contactMode);
    let accessControlPolicy = SchemaService.getOptionsListFromStaticData(staticData.accessControlPolicy);
    if (applicableContactModes.length) {
      contactMode = contactMode.filter(item => applicableContactModes.includes(item.value as ContactMode));
    }
    let modalHeaderText = "";
    if (data?.id) {
      modalHeaderText = copies.get("contact.context.update.header", { 0: staticData.contactMode[data.contactMode] });
    } else {
      modalHeaderText = copies.get("contact.context.create.header");
    }

    const adminIds = data?.admins?.map(item => item.id) || [];
    const licenseeIds = data?.licensees?.map(item => item.id) || [];
    return {
      modalHeaderText,
      accessControlPolicy,
      contactMode,
      locationType: applicableContactModes.includes(ContactMode.LEAD_CAPTURE)
        ? SchemaService.getOptionsListFromStaticData(staticData.contactContextLocation)
        : SchemaService.getOptionsListFromStaticData(staticData.contactContextLocation).filter(
            item => item.value !== ContactContextLocation.BOOTH
          ),
      admins:
        participants
          ?.filter(p => !adminIds.includes(p.id))
          .map(item => ({
            label: item.firstName + " " + item.lastName + " (" + item.jobTitle + ")",
            value: item.id
          })) || [],
      licensees:
        participants
          ?.filter(p => !licenseeIds.includes(p.id))
          .map(item => ({
            label: item.firstName + " " + item.lastName + " (" + item.jobTitle + ")",
            value: item.id
          })) || []
    };
  }, [
    data,
    participants,
    staticData.contactContextLocation,
    staticData.contactMode,
    staticData.accessControlPolicy,
    applicableContactModes,
    copies
  ]);

  const participantMap = React.useMemo(() => {
    return {
      ...(keyBy(data?.admins, "id") || {}),
      ...(keyBy(data?.licensees, "id") || {})
    };
  }, [data]);

  const validationSchema = React.useMemo(() => {
    return yup.object().shape({
      [leadCaptureFormFields.LABEL]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [leadCaptureFormFields.CONTACT_CONTEXT_LOCATION]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [leadCaptureFormFields.CONTACT_MODE]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [leadCaptureFormFields.LICENSE_CAP]: Validation.textFieldValidation({
        copies,
        required: true,
        min: MIN_LICENSE_CAP_FOR_CONTACT_CONTEXT,
        max: MAX_LICENSE_CAP_FOR_CONTACT_CONTEXT
      }),
      [leadCaptureFormFields.LICENSE_CRITERIA_DESCRIPTION]: Validation.textAreaValidation({
        copies,
        required: false,
        min: MIN_DEFAULT_TEXTAREA_LENGTH,
        max: MAX_DEFAULT_TEXTAREA_LENGTH
      }),
      [leadCaptureFormFields.ACCESS_CRITERIA_DESCRIPTION]: Validation.textAreaValidation({
        copies,
        required: false,
        min: MIN_DEFAULT_TEXTAREA_LENGTH,
        max: MAX_DEFAULT_TEXTAREA_LENGTH
      }),
      [leadCaptureFormFields.CONTACT_MODE]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [leadCaptureFormFields.LICENSEES]: Validation.yupIsReqArr({ copies, required: false })
        .test("LicenseCapShouldBeDefined", copies.get("contact.context.licensecap.not.set"), function (value: any) {
          const cap: any = this.resolve(yup.ref(leadCaptureFormFields.LICENSE_CAP));
          if (cap === undefined) {
            return false;
          }
          return true;
        })
        .test(
          "licesneCapTest",
          copies.get("contact.context.licensecap.exceeded.validation.message"),
          function (value: any) {
            const cap: any = +(this.resolve(yup.ref(leadCaptureFormFields.LICENSE_CAP)) || "0");
            const unlimited = this.resolve(yup.ref(leadCaptureFormFields.UNLIMITED_LICENSE_CAP));

            if (unlimited || cap === -1) {
              return true;
            }

            return value?.length <= cap;
          }
        ),
      [leadCaptureFormFields.LICENSE_CRITERIA]: yup
        .object()
        .nullable()
        .when("licenseeCriteria_enabled", {
          is: (enabled: boolean) => {
            return enabled;
          },
          then: yup
            .object()
            .nullable()
            .test("LicenseCriteriaTest", getYupTestForQuery(copies, { isOptional: false }))
        }),
      [leadCaptureFormFields.ACCESS_CRITERIA]: yup
        .object()
        .nullable()
        .when("accessCriteria_enabled", {
          is: (enabled: boolean) => {
            return enabled;
          },
          then: yup
            .object()
            .nullable()
            .test("AccessCriteriaTest", getYupTestForQuery(copies, { isOptional: false }))
        })
    });
  }, [copies]);

  const handleAttendeeListDownload = React.useCallback(async () => {
    downloadAttendeelist({
      registrationId: registrationId,
      contextEntityId: eventId,
      reportType: ReportType.LEAD_GENERATION,
      metaData: {
        contactContextId: contextId
      }
    }).then(obj => {
      const reportName = copies.get(
        data?.contactMode === ContactMode.LEAD_CAPTURE ? "lead.report.file.name" : "attendance.report.file.name",
        {
          0: data?.label,
          1: eventDetails?.name,
          2: DateTimeHelper.getDateTimeForReport()
        }
      );

      downloadFileUsingData(obj, reportName);
    });
  }, [
    contextId,
    copies,
    data?.contactMode,
    data?.label,
    downloadAttendeelist,
    eventDetails?.name,
    eventId,
    registrationId
  ]);

  return {
    handleCreateUpdate,
    initialValues,
    options,
    leadLocation: data,
    participantMap,
    participants,
    validationSchema,
    contextId,
    isFormDisabled,
    setIsFormDisabled,
    handleStatusUpdate,
    handleAttendeeListDownload,
    formRef
  };
}
